import {
  ActionIcon,
  Button,
  Flex,
  Group,
  PasswordInput,
  SimpleGrid,
  Stack,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import ButtonSave from '../../../../../components/ButtonSave';
import LabelText from '../../../../../components/LabelText/LabelText';
import { useI18n } from '../../../../../contexts/I18nProvider';
import ModuleWithParamsService from '../../../../../services/ModuleWithParamsService';
import type {
  ModuleDetails,
  SynchroOpenpayeModuleResponse,
  SynchroOpenpayeParams,
} from '../../../../../types/api/response/module';

interface OpenpayeApiKey {
  username: string;
  password: string;
}

interface FormValues {
  apiKeys: OpenpayeApiKey[];
}

type Props = {
  module: ModuleDetails | null;
  moduleParams: SynchroOpenpayeParams;
  setModuleParams: React.Dispatch<React.SetStateAction<SynchroOpenpayeParams>>;
  companyId: string;
};

export default function SynchroOpenpayefeatureConnectionForm({
  module,
  moduleParams,
  setModuleParams,
  companyId,
}: Props) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    initialValues: {
      apiKeys:
        moduleParams?.domains?.map((item) => ({
          username: item.username,
          password: item.password,
        })) || [],
    },
  });

  function handleUpdateOpenpayeParamsSubmit(
    values: ReturnType<(values: FormValues) => FormValues>,
    isTest: boolean
  ) {
    if (module) {
      updateModuleParamsOpenpaye({
        apiUrl: module.description.url,
        payload: {
          active: module?.active ? module.active : false,
          isTest,
          apiKeys: values.apiKeys,
        },
      });
    }
  }

  const { mutate: updateModuleParamsOpenpaye, isLoading } = useMutation({
    mutationFn: (variables: { apiUrl: string; payload: any }) =>
      ModuleWithParamsService.updateModuleParams(
        variables.apiUrl,
        companyId,
        variables.payload
      ),
    onSuccess: (_data: SynchroOpenpayeModuleResponse, _variables) => {
      showNotification({
        id: `success-update-module-params`,
        message: Boolean(_variables?.payload?.isTest)
          ? t('openpaye.dontForgotToSaveCredentials')
          : t('w.success'),
        color: Boolean(_variables?.payload?.isTest) ? 'orange' : 'green',
        icon: <IconCheck />,
      });
      queryClient.invalidateQueries({
        queryKey: ['CkModuleManagerServiceMS.list', companyId],
      });
      setModuleParams(_data.params);
    },
    onError: (error: any) => {
      if (error?.response?.data?.error_key === 'credentialInvalid') {
        showNotification({
          id: `error-update-module-params`,
          title: t('w.error'),
          message: t('openpaye.credentialsNotValid'),
          color: 'red',
          icon: <IconX />,
        });
      } else {
        showNotification({
          id: `error-update-module-params`,
          title: t('w.error'),
          message: t('error.updateModule'),
          color: 'red',
          icon: <IconX />,
        });
      }
    },
  });

  const fields = form.values.apiKeys.map((_item, index) => (
    <Group key={index} align="flex-start" mt="sm">
      <SimpleGrid cols={2} style={{ flex: 1 }}>
        <TextInput
          required
          placeholder={t('w.username')}
          label={index === 0 ? <LabelText text={t('w.username')} /> : null}
          {...form.getInputProps(`apiKeys.${index}.username`)}
        />
        <PasswordInput
          required
          placeholder={t('w.yourPassword')}
          label={index === 0 ? <LabelText text={t('w.password')} /> : null}
          {...form.getInputProps(`apiKeys.${index}.password`)}
        />
      </SimpleGrid>

      <ActionIcon
        color="red"
        variant="subtle"
        onClick={() => form.removeListItem('apiKeys', index)}
        mt={index === 0 ? 28 : 4}
        disabled={form.values.apiKeys.length === 1 && index === 0}
      >
        <IconTrash size="1rem" />
      </ActionIcon>
    </Group>
  ));

  return (
    <form
      onSubmit={form.onSubmit((values) =>
        handleUpdateOpenpayeParamsSubmit(values, false)
      )}
    >
      <Stack>
        {fields}

        <Button
          variant="light"
          leftSection={<IconPlus size="1rem" />}
          onClick={() =>
            form.insertListItem('apiKeys', { username: '', password: '' })
          }
          w="fit-content"
        >
          {t('w.add')}
        </Button>
      </Stack>
      <Flex
        justify={'flex-end'}
        gap={'md'}
        direction="row"
        wrap="wrap"
        mt={'md'}
      >
        <Button
          variant={'outline'}
          onClick={() =>
            handleUpdateOpenpayeParamsSubmit({ ...form.values }, true)
          }
          loading={isLoading}
        >
          {t('w.test')}
        </Button>
        <ButtonSave isLoading={isLoading} />
      </Flex>
    </form>
  );
}
